import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router";
import { LogOut, Home, Menu } from "lucide-react";
import { TISAButton } from "@/components/tisa/TISAButton";
import { useState } from "react";

interface AppHeaderProps {
  title?: string;
  showNav?: boolean;
  onBack?: () => void;
}

export function AppHeader({ title, showNav = true }: AppHeaderProps) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showMenu, setShowMenu] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleHome = () => {
    if (user?.role === "student") {
      navigate("/student/dashboard");
    } else if (user?.role === "teacher") {
      navigate("/teacher/dashboard");
    } else if (user?.role === "librarian") {
      navigate("/librarian/dashboard");
    }
  };

  return (
    <header className="bg-white shadow-sm border-b-2 border-tisa-blue-100 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo & Title Section */}
          <div className="flex items-center gap-4">
            {/* TISA Logo */}
            <img
              src="/images/tisa-logo.png"
              alt="TISA Library"
              className="h-12 w-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              onClick={handleHome}
              title="Go to home"
            />

            {/* Title or User Name */}
            {title ? (
              <h1 className="text-child-xl font-bold text-primary hidden sm:block">
                {title}
              </h1>
            ) : user ? (
              <div className="hidden sm:block">
                <p className="text-child-sm text-muted-foreground">Welcome,</p>
                <p className="text-child-lg font-semibold text-primary">
                  {user.name}
                </p>
              </div>
            ) : null}
          </div>

          {/* Navigation & Controls */}
          {showNav && user && (
            <nav className="flex items-center gap-2 sm:gap-4">
              {/* Desktop Menu */}
              <div className="hidden sm:flex items-center gap-2">
                <TISAButton
                  variant="outline"
                  size="sm"
                  onClick={handleHome}
                  icon={<Home className="h-4 w-4" />}
                >
                  Home
                </TISAButton>

                <TISAButton
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  icon={<LogOut className="h-4 w-4" />}
                >
                  Logout
                </TISAButton>
              </div>

              {/* Mobile Menu Button */}
              <div className="sm:hidden">
                <button
                  onClick={() => setShowMenu(!showMenu)}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  aria-label="Toggle menu"
                >
                  <Menu className="h-5 w-5 text-primary" />
                </button>
              </div>
            </nav>
          )}
        </div>

        {/* Mobile Menu */}
        {showMenu && user && (
          <div className="sm:hidden border-t pb-4 space-y-2">
            <TISAButton
              variant="outline"
              size="sm"
              className="w-full justify-center"
              onClick={() => {
                handleHome();
                setShowMenu(false);
              }}
              icon={<Home className="h-4 w-4" />}
            >
              Home
            </TISAButton>

            <TISAButton
              variant="outline"
              size="sm"
              className="w-full justify-center"
              onClick={() => {
                handleLogout();
                setShowMenu(false);
              }}
              icon={<LogOut className="h-4 w-4" />}
            >
              Logout
            </TISAButton>
          </div>
        )}
      </div>
    </header>
  );
}
